// ============================================================================
// organizationProfile.js
//
// Updated: SNOW support via injected snApi. Call setSNApiForOrganization(snApi)
// at module load in the SN entry point to enable org profile retrieval.
// ============================================================================

let forgeModule = null;
const loadForgeModule = async () => {
  if (!forgeModule) forgeModule = await import('@forge/bridge');
  return forgeModule;
};

// ── SNOW API injection ───────────────────────────────────────────────────────
let _snApi = null;
export function setSNApiForOrganization(snApi) { _snApi = snApi; }

const LABELS = {
  company_name: 'Organization Name',
  industry: 'Industry',
  company_type: 'Type of Company',
  employee_band: 'Employees',
  jira_users: 'Jira Users',
  servicenow_users: 'ServiceNow Users',
  service_team_size: 'Service Management Team Size',
  monthly_ticket_volume: 'Monthly Ticket Volume',
  regions_served: 'Regions Served',
  compliance_level: 'Compliance / Regulatory Pressure',
  operating_model: 'Operating Model',
  automation_appetite: 'Automation Appetite',
  notes: 'Additional Context',
};

export const sanitizeOrganizationProfile = (profile = {}) => {
  if (!profile || typeof profile !== 'object') return {};
  const cleaned = {};
  Object.entries(profile).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    const normalized = String(value).trim();
    if (!normalized) return;
    cleaned[key] = normalized;
  });
  return cleaned;
};

export const hasOrganizationProfile = (profile = {}) => Object.keys(sanitizeOrganizationProfile(profile)).length > 0;

export const toOrganizationPromptContext = (profile = {}) => {
  const cleaned = sanitizeOrganizationProfile(profile);
  const rows = Object.entries(cleaned).map(([key, value]) => ({
    field: LABELS[key] || key,
    value,
  }));
  return {
    available: rows.length > 0,
    fields: rows,
    profile: cleaned,
  };
};

export const fetchOrganizationProfile = async () => {
  try {
    // Web prompts receive Workspace Business Context on the server.
    if (typeof window !== 'undefined' && window.__PA_WEB_AI_CONTEXT__) return {};
    // If snApi has been injected (SNOW), use it directly
    if (_snApi && typeof _snApi.getOrganizationProfile === 'function') {
      const res = await _snApi.getOrganizationProfile();
      return sanitizeOrganizationProfile(res?.organizationProfile || {});
    }
    // Otherwise use Forge invoke (works for FORGE; on SNOW with stub returns {} which is harmless)
    const { invoke } = await loadForgeModule();
    const res = await invoke('getOrganizationProfile');
    return sanitizeOrganizationProfile(res?.organizationProfile || {});
  } catch (error) {
    console.warn('Unable to load organization profile for consultant prompts', error);
    return {};
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Prompt-injection helpers
//
// Every consultant AI prompt benefits from organization context (industry,
// team size, compliance posture, etc.). These helpers turn the saved profile
// into a labelled section and append it to any prompt string. Empty profiles
// produce an empty section, so the prompt is returned unchanged.
// ─────────────────────────────────────────────────────────────────────────────

// Turn a sanitized profile into a readable prompt section. Returns '' if empty.
export const buildOrganizationPromptSection = (profile = {}) => {
  const ctx = toOrganizationPromptContext(profile);
  if (!ctx.available) return '';
  const lines = ctx.fields.map(({ field, value }) => `- ${field}: ${value}`).join('\n');
  return `
ORGANIZATION CONTEXT
Use this context to tailor language, priorities, risk assessment, and recommendations to the customer's situation. Do not fabricate details beyond what is listed. Only populated fields are provided.
${lines}
`;
};

// Fetch the saved profile and append the organization context section to the
// given prompt string. Safe to call from any tab's generate() flow. If no
// profile is saved or an error occurs, the prompt is returned unchanged.
export const injectOrganizationContext = async (prompt) => {
  if (!prompt || typeof prompt !== 'string') return prompt;
  try {
    const profile = await fetchOrganizationProfile();
    const section = buildOrganizationPromptSection(profile);
    return section ? `${prompt}\n${section}` : prompt;
  } catch (error) {
    console.warn('injectOrganizationContext failed; continuing without org context', error);
    return prompt;
  }
};
